import { Dispatch, SetStateAction, useContext, useMemo } from 'react'
import { AddOutlined } from '@mui/icons-material'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'

import AddressForm from '../../../../components/address-form'
import ButtonLink from '../../../../components/button-link'
import Subsection from '../../../../components/subsection'
import { useHost } from '../../../../providers/app-provider'
import { useAuthentication } from '../../../../providers/auth-provider'
import Api from '../../../../utils/api'
import { GLOBAL_API } from '../../../../utils/constants'
import { formatAddress } from '../../../../utils/helpers'
import PeopleContext from '../../context'
import useStyles from '../../index.styles'

interface IAddressContentProps {
  isEditing: boolean
  setIsEditing: Dispatch<SetStateAction<boolean>>
}

const isAddressEmpty = (userInformation: IUserType['information']) => {
  const address = userInformation?.address
  if (!address) return true

  return `${address.address}${address.code}${address.country}${address.locality}${address.region}`.length === 0
}

const AddressContent = ({ isEditing, setIsEditing }: IAddressContentProps) => {
  const classes = useStyles

  const { user, setUser, loading } = useContext(PeopleContext)
  const { auth, hasInternalUserAccess } = useAuthentication()
  const host = useHost()

  const { enqueueSnackbar } = useSnackbar()

  const initialValues: AddressFormValues = useMemo(
    () => ({
      address: '',
      code: '',
      country: '',
      locality: '',
      region: '',
      ...user?.information?.address,
    }),
    [user?.information?.address]
  )

  const onSubmit = async (values: AddressFormValues) => {
    if (!host || !auth.user?.access_token || !user) return

    const validateAddressRes = await Api.get(
      `${GLOBAL_API}/api/v1/validate/address?address=${values.address}&region=${values.region}&country=${values.country}&code=${values.code}&locality=${values.locality}`
    )

    if (validateAddressRes.ok && validateAddressRes.body.isValid) {
      const res = await Api.put(`${host}/api/users/${user.id}`, {
        ...user,
        information: {
          ...user.information,
          address: {
            ...values,
          },
        },
      })

      if (res.ok) {
        setUser(res.body)
        setIsEditing(false)
      } else {
        enqueueSnackbar('Could not update address.', { variant: 'error' })
      }
    } else if (validateAddressRes.body.isValid === false) {
      enqueueSnackbar('Address is not valid', { variant: 'error' })
    } else if (!validateAddressRes.ok) {
      enqueueSnackbar('Could not validate address', { variant: 'error' })
    }
  }

  if (isEditing) {
    return (
      <div style={classes.subsectionContainer}>
        <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize={true}>
          {({ isSubmitting }) => (
            <Form>
              {/* user object does not have address2 */}
              <AddressForm hideAddress2={true} />
              <Box sx={classes.formButtonContainer}>
                <Button variant="contained" type="submit" disabled={isSubmitting}>
                  Confirm
                </Button>
                <Button onClick={() => setIsEditing(false)}>Cancel</Button>
              </Box>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  if (isAddressEmpty(user?.information)) {
    return hasInternalUserAccess ? (
      <div style={classes.subsectionContainer}>
        <ButtonLink startIcon={<AddOutlined />} onClick={() => setIsEditing(true)}>
          Add Address
        </ButtonLink>
      </div>
    ) : (
      <Typography
        sx={{
          marginTop: 1,
        }}
      >
        N/A
      </Typography>
    )
  }

  return (
    <div style={classes.subsectionContainer}>
      <Subsection
        header="Address"
        content={
          loading ? (
            <div>
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width={150} />
            </div>
          ) : (
            <div>
              {user?.information?.address?.address && (
                <Typography variant="body1" className="fs-exclude">
                  {formatAddress(`${user.information.address.address}`, user.information.address.country)}
                </Typography>
              )}
              {(user?.information?.address?.locality || user?.information?.address?.region || user?.information?.address?.code) && (
                <Typography variant="body1" className="fs-exclude">
                  {formatAddress(
                    `${user.information.address.locality}, ${user.information.address.region} ${user.information.address.code}`,
                    user.information.address.country
                  )}
                </Typography>
              )}
              {user?.information?.address?.country && (
                <Typography variant="body1" className="fs-exclude">
                  {formatAddress(user?.information.address.country, user?.information.address.country)}
                </Typography>
              )}
            </div>
          )
        }
        loading={loading}
        hideSkeleton={true}
      />
    </div>
  )
}

export default AddressContent
